import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal } from "@angular/core";
import { Router, RouterLink, isActive } from "@angular/router";
import { menuData } from "./menu-data";

@Component({
  selector: "app-sidebar",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  host: {
    class: "contents lg:block lg:flex-none lg:h-full"
  },
  templateUrl: "./sidebar.component.html"
})
export class SidebarComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly desktopQuery = typeof window === "undefined"
    ? null
    : window.matchMedia("(min-width: 1024px)");

  collapsed = signal(false);
  mobileOpen = input(false);
  mobileClose = output<void>();
  isDesktop = signal(this.desktopQuery?.matches ?? true);
  isCollapsed = computed(() => this.isDesktop() && this.collapsed());
  private readonly activeMatchOptions = {
    paths: "exact" as const,
    queryParams: "ignored" as const,
    matrixParams: "ignored" as const,
    fragment: "ignored" as const
  };
  
  currentTheme = signal<'tramitex' | 'tramitexdark'>(this.getStoredTheme());
  readonly menuItems = menuData.map((item) => ({
    ...item,
    isActive: isActive(item.ruta, this.router, this.activeMatchOptions)
  }));

  constructor() {
    if (!this.desktopQuery) {
      return;
    }

    const updateDesktop = (event?: MediaQueryListEvent) => {
      this.isDesktop.set(event?.matches ?? this.desktopQuery?.matches ?? true);
    };

    updateDesktop();
    this.desktopQuery.addEventListener("change", updateDesktop);
    this.destroyRef.onDestroy(() => this.desktopQuery?.removeEventListener("change", updateDesktop));
    
    // Aplicar tema inicial
    this.applyTheme(this.currentTheme());
    
    // Observar cambios de tema
    effect(() => {
      const theme = this.currentTheme();
      this.applyTheme(theme);
      localStorage.setItem('tramitex-theme', theme);
    });
  }
  
  toggleTheme() {
    this.currentTheme.set(this.currentTheme() === 'tramitex' ? 'tramitexdark' : 'tramitex');
  }
  
  private applyTheme(theme: 'tramitex' | 'tramitexdark') {
    const html = document.documentElement;
    html.setAttribute('data-theme', theme);
  }
  
  private getStoredTheme(): 'tramitex' | 'tramitexdark' {
    if (typeof window === 'undefined') return 'tramitex';
    const stored = localStorage.getItem('tramitex-theme');
    return (stored as 'tramitex' | 'tramitexdark') || 'tramitex';
  }
}
